import React, { useEffect, useState, createRef } from 'react';

import { CcLoader } from '@utp/components-react';
import Icon from '../../components/Icon';
import IDHelpers from '../../../core/helpers/IDHelper';

import { useOutsideClick } from './helpers';
import {
  WrapperSelect,
  WrapperSelectButton,
  WrapperSelectItem,
  WrapperSelectItems,
  WrapperSelectLabel,
  WrapperSelectRelative
} from './styles';

interface IProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  itemCount: number;
  itemName: string;
  items: any[];
  itemValue: string;
  label: string;
  onChange: (v: any) => void;
  placeholder: string;
  returnObject?: boolean;
  value: any;
  error?: boolean;
}

function Select({
  isDisabled,
  isLoading,
  itemCount,
  itemName,
  items,
  itemValue,
  label,
  onChange,
  placeholder,
  returnObject,
  value,
  error = false
}: Readonly<IProps>) {
  const buttonRef = createRef<HTMLButtonElement>();

  const [data, setData] = useState<any[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isTop, setIsTop] = useState<boolean>(false);
  const [selected, setSelected] = useState<string>('');

  const ref = useOutsideClick(() => {
    setIsActive(false);
  });

  const handlerClick = () => {
    if (!(isDisabled || isLoading || data?.length === 0)) {
      if (buttonRef.current) {
        const { bottom } = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const bottomPosition = itemCount * 40 + bottom + 16;
        setIsTop(bottomPosition > windowHeight);
        setIsActive(!isActive);
      }
    }
  };

  const formatItem = (v: any) => ({
    value: v ? v[itemValue] : '',
    name: v ? v[itemName] : ''
  });

  const sendFormatItem = (v: any) => ({
    [itemValue]: v.value,
    [itemName]: v.name
  });

  useEffect(() => {
    if (items?.length > 0) {
      const itemsMap = items.map((v) => formatItem(v));

      setData(itemsMap);

      const itemFind = itemsMap.find((v) =>
        returnObject
          ? JSON.stringify(v) === JSON.stringify(formatItem(value))
          : v.value === value
      );

      if (itemFind) {
        setIsSelected(true);
        setSelected(itemFind?.name);
      } else {
        setIsSelected(false);
        setSelected('');
      }
    }

    if (items?.length === 0) {
      setData([]);
    }
  }, [items, value]);

  return (
    <WrapperSelect>
      <WrapperSelectLabel disabled={data?.length === 0}>
        {label}
      </WrapperSelectLabel>
      <WrapperSelectRelative ref={ref}>
        <WrapperSelectButton
          error={error}
          data-testid="cmp-select-button-toggle"
          ref={buttonRef}
          disabled={isDisabled || isLoading || data?.length === 0}
          isActive={isActive}
          isSelected={isSelected}
          onClick={() => {
            handlerClick();
          }}
        >
          <span data-testid="cmp-select-selectedValue">
            {selected || placeholder}
          </span>
          <div>
            {isLoading ? (
              <CcLoader size={24} />
            ) : (
              <Icon name={isActive ? 'chevron-up' : 'chevron-down'} size={24} />
            )}
          </div>
        </WrapperSelectButton>
        <WrapperSelectItems
          isActive={isActive}
          isTop={isTop}
          itemCount={itemCount}
        >
          {data?.map((v) => (
            <WrapperSelectItem
              data-testid="cmp-select-button-item"
              isActive={
                returnObject
                  ? JSON.stringify(v) === JSON.stringify(formatItem(value))
                  : v.value === value
              }
              key={IDHelpers.generateID()}
              onClick={() => {
                if (v.value === value?.value) {
                  setIsActive(false);
                  return;
                }
                if (returnObject) onChange(sendFormatItem(v));
                else onChange(v.value);
                setIsActive(false);
              }}
            >
              <span>{v.name}</span>
            </WrapperSelectItem>
          ))}
        </WrapperSelectItems>
        {error && (
          <div className="errorText">
            <Icon name="alert-triangle" size={14} />
            {'Este curso no tiene nota a rectificar.'}
          </div>
        )}
      </WrapperSelectRelative>
    </WrapperSelect>
  );
}

export default Select;
